import React, { useState, useEffect } from 'react';
import './SpeakerLabel.css';

/**
 * Convert SPEAKER_00 format to "Speaker 1" format (1-indexed)
 */
const humanizeSpeakerLabel = (label) => {
  if (!label) return label;
  const match = label.match(/SPEAKER_(\d+)/);
  if (match) {
    // SPEAKER_00 becomes Speaker 1, SPEAKER_01 becomes Speaker 2, etc.
    const speakerNumber = parseInt(match[1], 10) + 1;
    return `Speaker ${speakerNumber}`;
  }
  return label;
};

/**
 * SpeakerLabel component for displaying and managing speaker information in transcripts
 * Supports linking to speaker profiles, renaming, and creating new speakers
 */
function SpeakerLabel({
  speaker,              // The raw speaker label (e.g., "SPEAKER_00")
  transcriptId,         // Transcript ID for API calls
  transcriptSpeakers,   // Array of transcript-speaker links from API
  availableSpeakers,    // Array of all user speakers for matching
  onSpeakerUpdated,     // Callback when speaker is updated
  onError               // Callback for error messages (optional)
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [newSpeakerName, setNewSpeakerName] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [matches, setMatches] = useState([]);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Ensure transcriptSpeakers and availableSpeakers are arrays
  const safeTranscriptSpeakers = Array.isArray(transcriptSpeakers) ? transcriptSpeakers : [];
  const safeAvailableSpeakers = Array.isArray(availableSpeakers) ? availableSpeakers : [];

  // Find the transcript-speaker link for this speaker
  const transcriptSpeaker = safeTranscriptSpeakers.find(ts => ts.localLabel === speaker);

  // Get the display name (custom label, speaker name, or humanized label)
  const displayName = transcriptSpeaker?.customLabel ||
                      transcriptSpeaker?.speakerName ||
                      humanizeSpeakerLabel(speaker);

  console.log('[SPEAKER LABEL] Render:', {
    speaker,
    transcriptSpeaker,
    displayName,
    transcriptSpeakersCount: safeTranscriptSpeakers.length
  });

  const speakerColor = transcriptSpeaker?.speakerColor || '#666';
  const confidence = transcriptSpeaker?.confidence;
  const isLinked = !!transcriptSpeaker?.speakerId;

  useEffect(() => {
    if (transcriptSpeaker?.customLabel) {
      setCustomLabel(transcriptSpeaker.customLabel);
    } else {
      // Set default humanized label if no custom label exists
      setCustomLabel(humanizeSpeakerLabel(speaker));
    }
  }, [transcriptSpeaker, speaker]);

  const handleLinkToSpeaker = async (speakerId) => {
    try {
      const response = await fetch(`/api/transcripts/${transcriptId}/speakers/${speaker}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ speakerId, confidence: 1.0 })
      });

      if (response.ok) {
        setIsMenuOpen(false);
        if (onSpeakerUpdated) onSpeakerUpdated();
      }
    } catch (err) {
      console.error('Error linking speaker:', err);
    }
  };

  const handleCreateAndLink = async () => {
    if (!newSpeakerName.trim()) return;

    try {
      // Create new speaker
      const createResponse = await fetch('/api/speakers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newSpeakerName.trim() })
      });

      if (createResponse.ok) {
        const newSpeaker = await createResponse.json();

        // Link to transcript
        await handleLinkToSpeaker(newSpeaker.id);
        setNewSpeakerName('');
        setIsCreating(false);
      }
    } catch (err) {
      console.error('Error creating speaker:', err);
    }
  };

  const showError = (message) => {
    setErrorMessage(message);
    // Auto-clear after 5 seconds
    setTimeout(() => setErrorMessage(null), 5000);
    // Also call external handler if provided
    if (onError) {
      onError(message);
    }
  };

  const handleUpdateCustomLabel = async () => {
    setErrorMessage(null);
    try {
      console.log('[SPEAKER LABEL] Updating speaker:', speaker, 'to:', customLabel.trim());
      const response = await fetch(`/api/transcripts/${transcriptId}/speakers/${speaker}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ customLabel: customLabel.trim() })
      });

      if (response.ok) {
        console.log('[SPEAKER LABEL] Update successful, calling onSpeakerUpdated callback');
        setIsMenuOpen(false);
        if (onSpeakerUpdated) {
          onSpeakerUpdated();
        } else {
          console.warn('[SPEAKER LABEL] No onSpeakerUpdated callback provided');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Failed to update speaker label:', response.status, errorData);
        showError(errorData.error || 'Failed to update speaker name');
      }
    } catch (err) {
      console.error('Error updating custom label:', err);
      showError(`Error updating speaker name: ${err.message}`);
    }
  };

  const handleUnlink = async () => {
    try {
      const response = await fetch(`/api/transcripts/${transcriptId}/speakers/${speaker}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        setIsMenuOpen(false);
        if (onSpeakerUpdated) onSpeakerUpdated();
      }
    } catch (err) {
      console.error('Error unlinking speaker:', err);
    }
  };

  const handleMergeInto = async (targetLabel) => {
    try {
      const response = await fetch(`/api/transcripts/${transcriptId}/speakers/merge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fromLabel: speaker, toLabel: targetLabel })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[SPEAKER LABEL] Merge successful:', result);
        setIsMenuOpen(false);
        setIsMerging(false);
        if (onSpeakerUpdated) onSpeakerUpdated();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        showError(errorData.error || 'Failed to merge speakers');
      }
    } catch (err) {
      console.error('Error merging speakers:', err);
      showError(`Error merging speakers: ${err.message}`);
    }
  };

  const loadMatches = async () => {
    if (!transcriptSpeaker?.embedding) return;

    setIsLoadingMatches(true);
    try {
      const response = await fetch(`/api/transcripts/${transcriptId}/speakers/match`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          speakerEmbeddings: {
            [speaker]: {
              embedding: transcriptSpeaker.embedding,
              shape: transcriptSpeaker.embeddingShape,
              dtype: transcriptSpeaker.embeddingDtype || 'float32'
            }
          },
          threshold: 0.7
        })
      });

      if (response.ok) {
        const matchData = await response.json();
        setMatches(matchData[speaker]?.matches || []);
      }
    } catch (err) {
      console.error('Error loading speaker matches:', err);
    } finally {
      setIsLoadingMatches(false);
    }
  };

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
    if (!isLinked && transcriptSpeaker?.embedding) {
      loadMatches();
    }
  };

  const handleContextMenu = (e) => {
    // Prevent the default context menu and open speaker menu instead
    e.preventDefault();
    e.stopPropagation();
    handleMenuOpen();
  };

  return (
    <div className="speaker-label-container">
      <button
        className={`speaker-label-button ${isLinked ? 'linked' : 'unlinked'}`}
        onClick={handleMenuOpen}
        onContextMenu={handleContextMenu}
        style={{ borderLeftColor: speakerColor }}
      >
        <span className="speaker-color-dot" style={{ backgroundColor: speakerColor }} />
        <span className="speaker-name">{displayName}</span>
        {isLinked && confidence !== undefined && confidence < 0.85 && (
          <span className="speaker-confidence">{Math.round(confidence * 100)}%</span>
        )}
      </button>

      {isMenuOpen && (
        <div className="speaker-menu-overlay" onClick={() => setIsMenuOpen(false)}>
          <div className="speaker-menu" onClick={(e) => e.stopPropagation()}>
            <div className="speaker-menu-header">
              <h3>Manage Speaker</h3>
              <button className="close-button" onClick={() => setIsMenuOpen(false)}>×</button>
            </div>

            <div className="speaker-menu-content">
              {/* Error Message */}
              {errorMessage && (
                <div className="speaker-error-message">
                  <span className="error-icon">⚠</span>
                  <span>{errorMessage}</span>
                  <button className="error-dismiss" onClick={() => setErrorMessage(null)}>×</button>
                </div>
              )}

              {/* Rename Section */}
              <div className="menu-section">
                <label>Custom Name</label>
                <div className="input-group">
                  <input
                    type="text"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder={speaker}
                  />
                  <button onClick={handleUpdateCustomLabel} className="action-button">
                    Update
                  </button>
                </div>
              </div>

              {/* Link to Existing Speaker */}
              {!isLinking && !isCreating && (
                <div className="menu-section">
                  <label>Link to Profile</label>
                  {isLinked ? (
                    <div className="linked-speaker-info">
                      <div className="linked-speaker-name">
                        <span className="speaker-color-dot" style={{ backgroundColor: speakerColor }} />
                        {transcriptSpeaker.speakerName}
                      </div>
                      <button onClick={handleUnlink} className="unlink-button">
                        Unlink
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setIsLinking(true)} className="action-button">
                      Link to Existing
                    </button>
                  )}
                </div>
              )}

              {/* Linking Mode */}
              {isLinking && (
                <div className="menu-section">
                  <label>Select Speaker Profile</label>
                  {isLoadingMatches ? (
                    <div className="loading-matches">Loading suggestions...</div>
                  ) : matches.length > 0 ? (
                    <div className="speaker-matches">
                      <p className="matches-label">Suggested matches:</p>
                      {matches.map(match => (
                        <button
                          key={match.speakerId}
                          onClick={() => handleLinkToSpeaker(match.speakerId)}
                          className="speaker-match-item"
                        >
                          <span className="speaker-color-dot" style={{ backgroundColor: match.color }} />
                          <span className="match-name">{match.speakerName}</span>
                          <span className="match-confidence">{Math.round(match.similarity * 100)}%</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="no-matches">No matching speakers found</p>
                  )}

                  <div className="all-speakers-list">
                    <p className="matches-label">All speakers:</p>
                    {safeAvailableSpeakers.map(sp => (
                      <button
                        key={sp.id}
                        onClick={() => handleLinkToSpeaker(sp.id)}
                        className="speaker-match-item"
                      >
                        <span className="speaker-color-dot" style={{ backgroundColor: sp.color }} />
                        <span className="match-name">{sp.name}</span>
                      </button>
                    ))}
                  </div>

                  <button onClick={() => setIsLinking(false)} className="cancel-button">
                    Cancel
                  </button>
                </div>
              )}

              {/* Create New Speaker */}
              {!isLinking && !isCreating && !isMerging && !isLinked && (
                <div className="menu-section">
                  <button onClick={() => setIsCreating(true)} className="action-button create-button">
                    Create New Profile
                  </button>
                </div>
              )}

              {/* Creating Mode */}
              {isCreating && (
                <div className="menu-section">
                  <label>New Speaker Name</label>
                  <div className="input-group">
                    <input
                      type="text"
                      value={newSpeakerName}
                      onChange={(e) => setNewSpeakerName(e.target.value)}
                      placeholder="Enter speaker name"
                      autoFocus
                    />
                    <button onClick={handleCreateAndLink} className="action-button">
                      Create
                    </button>
                  </div>
                  <button onClick={() => setIsCreating(false)} className="cancel-button">
                    Cancel
                  </button>
                </div>
              )}

              {/* Merge Into Another Speaker (within this transcript) */}
              {!isLinking && !isCreating && !isMerging && safeTranscriptSpeakers.length > 1 && (
                <div className="menu-section merge-section">
                  <label>Merge Speakers</label>
                  <button onClick={() => setIsMerging(true)} className="action-button merge-button">
                    Merge Into Another Speaker
                  </button>
                  <p className="merge-hint">Combine all segments from this speaker into another speaker in this transcript</p>
                </div>
              )}

              {/* Merging Mode */}
              {isMerging && (
                <div className="menu-section">
                  <label>Merge "{displayName}" into:</label>
                  <div className="merge-targets-list">
                    {safeTranscriptSpeakers
                      .filter(ts => ts.localLabel !== speaker)
                      .map(ts => {
                        const targetDisplayName = ts.customLabel || ts.speakerName || humanizeSpeakerLabel(ts.localLabel);
                        return (
                          <button
                            key={ts.localLabel}
                            onClick={() => handleMergeInto(ts.localLabel)}
                            className="speaker-match-item merge-target"
                          >
                            <span className="speaker-color-dot" style={{ backgroundColor: ts.speakerColor || '#666' }} />
                            <span className="match-name">{targetDisplayName}</span>
                          </button>
                        );
                      })
                    }
                  </div>
                  <button onClick={() => setIsMerging(false)} className="cancel-button">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SpeakerLabel;
